import { apiRequest } from "util/services";
import { ADVISOR_MAX_CONTEXT_TOKENS } from "util/copilotContextUsage";
import { deriveAnalyticsScopeFromMessages } from "util/advisorAnalyticsScope";

export const mapApiSessionSummary = (session) => ({
  id: session.session_id,
  title: session.title || "New chat",
  titleIsCustom: session.title_is_custom === true,
  messages: [],
  messagesLoaded: false,
  updatedAt: session.updated_at ? Date.parse(session.updated_at) : Date.now(),
  contextUsedTokens: session.context_used_tokens ?? 0,
  contextMaxTokens: session.context_max_tokens ?? ADVISOR_MAX_CONTEXT_TOKENS,
  analyticsScope: null,
});

export const mapApiSessionDetail = (session) => {
  const messages = Array.isArray(session.messages) ? session.messages : [];
  const messageFeedback = session.message_feedback && typeof session.message_feedback === "object"
    ? { ...session.message_feedback }
    : {};
  return {
    ...mapApiSessionSummary(session),
    messages,
    messagesLoaded: true,
    messageFeedback,
    rating: session.rating && typeof session.rating === "object" ? { ...session.rating } : null,
    updatedAt: session.updated_at ? Date.parse(session.updated_at) : Date.now(),
    analyticsScope: deriveAnalyticsScopeFromMessages(messages),
  };
};

export const mapClientSessionForImport = (session) => ({
  id: session.id,
  title: session.title,
  messages: session.messages || [],
  updatedAt: session.updatedAt,
  context_used_tokens: session.contextUsedTokens ?? 0,
  context_max_tokens: session.contextMaxTokens ?? ADVISOR_MAX_CONTEXT_TOKENS,
});

export const mapClientSessionForPut = (session, { lastRoute, contextUsage } = {}) => ({
  title: session.title,
  messages: (session.messages || []).map((msg) => {
    const row = {
      role: msg.role,
      content: msg.content ?? "",
    };
    if (msg.ts != null) row.ts = msg.ts;
    if (msg.isPlainText != null) row.isPlainText = msg.isPlainText;
    if (msg.isHTML != null) row.isHTML = msg.isHTML;
    if (Array.isArray(msg.suggestedFollowups) && msg.suggestedFollowups.length) {
      row.suggestedFollowups = msg.suggestedFollowups;
    }
    if (typeof msg.guidance === "string" && msg.guidance.trim()) {
      row.guidance = msg.guidance.trim();
    }
    if (
      msg.dashboardPicker &&
      typeof msg.dashboardPicker === "object" &&
      Array.isArray(msg.dashboardPicker.dashboards) &&
      msg.dashboardPicker.dashboards.length
    ) {
      row.dashboardPicker = msg.dashboardPicker;
    }
    if (typeof msg.reasoning === "string" && msg.reasoning.trim()) {
      row.reasoning = msg.reasoning.trim();
    }
    if (Array.isArray(msg.toolActions) && msg.toolActions.length) {
      row.toolActions = msg.toolActions
        .filter((item) => typeof item === "string" && item.trim())
        .map((item) => item.trim());
    }
    if (
      msg.dashboardSelection &&
      typeof msg.dashboardSelection === "object" &&
      msg.dashboardSelection.id != null
    ) {
      row.dashboardSelection = {
        id: msg.dashboardSelection.id,
        ...(typeof msg.dashboardSelection.name === "string" &&
        msg.dashboardSelection.name.trim()
          ? { name: msg.dashboardSelection.name.trim() }
          : {}),
      };
    }
    return row;
  }),
  context_used_tokens: contextUsage?.usedTokens ?? session.contextUsedTokens ?? 0,
  context_max_tokens: contextUsage?.maxTokens ?? session.contextMaxTokens ?? ADVISOR_MAX_CONTEXT_TOKENS,
  last_route: lastRoute ?? null,
});

export const fetchCxGptSessions = (search = "") => {
  const q = search ? `?search=${encodeURIComponent(search)}` : "";
  return apiRequest(`dashboards/cx_gpt/sessions${q}`, "GET", null, true);
};

export const fetchCxGptSession = (sessionId) =>
  apiRequest(`dashboards/cx_gpt/sessions/${encodeURIComponent(sessionId)}`, "GET", null, true);

export const createCxGptSession = (body = {}) =>
  apiRequest("dashboards/cx_gpt/sessions", "POST", body, true);

export const updateCxGptSession = (sessionId, body) =>
  apiRequest(
    `dashboards/cx_gpt/sessions/${encodeURIComponent(sessionId)}`,
    "PUT",
    body,
    true
  );

export const renameCxGptSession = (sessionId, title) =>
  updateCxGptSession(sessionId, { title });

export const deleteCxGptSession = (sessionId) =>
  apiRequest(
    `dashboards/cx_gpt/sessions/${encodeURIComponent(sessionId)}`,
    "DELETE",
    null,
    true
  );

export const importCxGptSessions = (body) =>
  apiRequest("dashboards/cx_gpt/sessions/import", "POST", body, true);

export const fetchOrgCxGptSessions = (body) =>
  apiRequest("dashboards/cx_gpt/sessions/organization", "POST", body, true);

export const fetchOrgCxGptSession = (sessionId, ownerUserId) =>
  apiRequest(
    `dashboards/cx_gpt/sessions/organization/${encodeURIComponent(sessionId)}?owner_user_id=${encodeURIComponent(ownerUserId)}`,
    "GET",
    null,
    true
  );

export const deleteOrgCxGptSession = (sessionId, ownerUserId) =>
  apiRequest(
    `dashboards/cx_gpt/sessions/organization/${encodeURIComponent(sessionId)}?owner_user_id=${encodeURIComponent(ownerUserId)}`,
    "DELETE",
    null,
    true
  );

export const submitCxGptMessageFeedback = (body) =>
  apiRequest("dashboards/cx_gpt/messages/feedback", "POST", body, true);

/** End-of-chat rating: { stars: 1-5, comment? }. Sending again replaces it. */
export const submitCxGptSessionRating = (sessionId, body) =>
  apiRequest(
    `dashboards/cx_gpt/sessions/${encodeURIComponent(sessionId)}/rating`,
    "POST",
    body,
    true
  );

export const fetchOrgCxGptMessageFeedback = (body) =>
  apiRequest("dashboards/cx_gpt/messages/feedback/organization", "POST", body, true);

export const mapOrgApiSessionSummary = (session) => ({
  id: session.session_id,
  ownerUserId: session.user_id,
  title: session.title || "New chat",
  titleIsCustom: session.title_is_custom === true,
  updatedAt: session.updated_at ? Date.parse(session.updated_at) : Date.now(),
  messageCount: session.message_count ?? 0,
  lastRoute: session.last_route || null,
  user: session.user || {
    user_id: session.user_id,
    email: "",
    full_name: session.user_id,
    access_level: "r",
  },
});

export const mapOrgApiSessionDetail = (session) => ({
  ...mapOrgApiSessionSummary(session),
  messages: Array.isArray(session.messages) ? session.messages : [],
  contextUsedTokens: session.context_used_tokens ?? 0,
  contextMaxTokens: session.context_max_tokens ?? 16000,
});
